import type { SalesHeadlineOutput } from "@/features/tools/proToolTypes";

type SalesHeadlineResultProps = {
  output: SalesHeadlineOutput;
};

function ListBlock({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
        No items were returned for this section.
      </p>
    );
  }

  return (
    <ul className="grid gap-2 text-sm leading-6 text-slate-700">
      {items.map((item) => (
        <li key={item} className="min-w-0 break-words rounded-md bg-slate-50 p-3">
          {item}
        </li>
      ))}
    </ul>
  );
}

export function SalesHeadlineResult({ output }: SalesHeadlineResultProps) {
  return (
    <section className="min-w-0 rounded-lg border border-emerald-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-700">
            Sales headline result
          </p>
          <h3 className="mt-2 break-words text-2xl font-semibold tracking-normal text-slate-950">
            Recommended top pick
          </h3>
        </div>
        <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Headline angles generated
        </div>
      </div>

      <div className="mt-5 min-w-0 rounded-lg border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
          {output.recommendedTopPick.angleKey.replace(/_/g, " ")}
        </p>
        <p className="mt-2 break-words text-xl font-semibold leading-8 text-slate-950">
          {output.recommendedTopPick.headline}
        </p>
        <p className="mt-3 text-sm leading-6 text-slate-700">
          {output.recommendedTopPick.reason}
        </p>
      </div>

      <p className="mt-5 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
        {output.summary}
      </p>

      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-950">
          Headline angles
        </h4>
        <div className="mt-3 grid min-w-0 gap-4 lg:grid-cols-2">
          {output.headlines.map((headline) => (
            <article
              key={headline.angleKey}
              className="min-w-0 rounded-lg border border-slate-200 bg-white p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                {headline.angleLabel}
              </p>
              <h5 className="mt-2 break-words text-lg font-semibold leading-7 text-slate-950">
                {headline.headline}
              </h5>
              <p className="mt-2 break-words text-sm leading-6 text-slate-700">
                {headline.subheadline}
              </p>
              <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-600">
                <div>
                  <p className="font-semibold text-slate-950">Why it works</p>
                  <p className="break-words">{headline.whyItWorks}</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-950">Best use case</p>
                  <p className="break-words">{headline.bestUseCase}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            Positioning notes
          </h4>
          <div className="mt-3">
            <ListBlock items={output.positioningNotes} />
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            What to avoid
          </h4>
          <div className="mt-3">
            <ListBlock items={output.whatToAvoid} />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold text-slate-950">
          Recommended next step
        </h4>
        <p className="mt-2 text-sm leading-6 text-slate-700">
          {output.recommendedNextStep}
        </p>
      </div>
    </section>
  );
}
